import { Button as AntButton, type ButtonProps as AntButtonProps } from 'antd';
import classNames from 'classnames';
import React, { memo } from 'react';

import './Button.less';

/**
 * Implemented button paints. See docs/buttons.md for the full type mapping
 * (including secondary, which is not on this primitive yet).
 */
type ButtonType = 'primary' | 'tertiary' | 'danger' | 'danger-filled' | 'action';

type ButtonSize = 'large' | 'medium' | 'small';

type ButtonWidth = 'fit-content' | 'block' | number;

type ButtonProps = Pick<
  AntButtonProps,
  'disabled' | 'htmlType' | 'id' | 'loading' | 'onClick'
> & {
  children: string | JSX.Element;
  href?: AntButtonProps['href'];
  rel?: AntButtonProps['rel'];
  target?: AntButtonProps['target'];
  /** Default `medium`. */
  size?: ButtonSize;
  type: ButtonType;
  /** Default `'fit-content'`. `'block'` fills the parent; a number sets a fixed px slot with overflow cut. */
  width?: ButtonWidth;
};

const ANT_SIZE_MAP: Record<ButtonSize, AntButtonProps['size']> = {
  large: 'large',
  medium: 'middle',
  small: 'small'
};

function mapTypeToAntd(type: ButtonType): {
  antdType: AntButtonProps['type'];
  danger?: boolean;
} {
  switch (type) {
    case 'primary':
      return { antdType: 'primary' };
    case 'tertiary':
      return { antdType: 'default' };
    case 'danger':
      return { antdType: 'default', danger: true };
    case 'danger-filled':
      return { antdType: 'primary', danger: true };
    case 'action':
      return { antdType: 'primary' };
  }
}

function Button({
  children,
  disabled,
  href,
  htmlType,
  id,
  loading,
  onClick,
  rel,
  size = 'medium',
  target,
  type = 'primary',
  width = 'fit-content'
}: ButtonProps): JSX.Element {
  const { antdType, danger } = mapTypeToAntd(type);
  const isBlockWidth = width === 'block';
  const isFixedWidth = typeof width === 'number';

  return (
    <AntButton
      block={isBlockWidth}
      className={classNames(
        'Button',
        `Button--${type}`,
        `Button--${size}`,
        { 'Button--width-fixed': isFixedWidth }
      )}
      danger={danger}
      disabled={disabled}
      href={href}
      htmlType={htmlType}
      id={id}
      loading={loading}
      rel={rel}
      size={ANT_SIZE_MAP[size]}
      style={isFixedWidth ? { '--Button-width': `${width}px` } as React.CSSProperties : undefined}
      target={target}
      type={antdType}
      onClick={onClick}
    >
      {children}
    </AntButton>
  );
}

export default memo(Button);
export type { ButtonProps };
